import { FC, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import UploadIcon from '@mui/icons-material/Upload'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import TableChartIcon from '@mui/icons-material/TableChart'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import {
  BevolkingsdataMetaResponse,
  BevolkingsdataPivotResponse,
  GemeenteResponse,
} from '../../types/apiRequests'
import { getJson, getUrl } from '../../providers/data'
import authToken from '../../utils/authToken'
import ImportDialog from './ImportDialog'
import HistorischeDataChart from './HistorischeDataChart'
import GebiedTreeview from './GebiedTreeview'
import {
  DOELGROEP_LABELS,
  DoelgroepKey,
  aggregateByDoelgroep,
} from './doelgroepMappings'
import { DataPageLayout, FilterBar } from '../../components/layout'
import GemeenteAutocomplete from '../../components/inputs/GemeenteAutocomplete'
import { rememberGemeente, useLastGemeente } from '../../utils/useLastGemeente'
import { xxllncColor } from '../../layout/themes'

// Narrowed shape of the per-gebied entries in BevolkingsdataMetaResponse.gebied_codes.
// The generated schema is intentionally loose (`Record<string, any>[]`); the view
// only cares about `code` + `naam`.
interface GebiedNode {
  code: string
  naam: string
}

const STAD_CATEGORIE_LABELS: Record<string, string> = {
  pop: 'Bevolking',
  geb: 'Geboorte',
  ste: 'Sterfte',
  hh: 'Huishoudens',
  ves_bin: 'Binnenlandse vestiging',
  ves_bui: 'Buitenlandse vestiging',
  ver_bin: 'Binnenlands vertrek',
  ver_bui: 'Buitenlands vertrek',
  won: 'Woningvoorraad',
  hsr: 'Headshiprate',
  ng: 'Natuurlijke groei',
  bisal: 'Binnenlands migratiesaldo',
  busal: 'Buitenlands migratiesaldo',
  gwb: 'Gemiddelde woningbezetting',
  sum: 'Samenvatting',
}

const GEBIED_CATEGORIE_LABELS: Record<string, string> = {
  pop: 'Bevolking',
  popt: 'Bevolking (T)',
  geb: 'Geboorte',
  won: 'Woningvoorraad',
  gwb: 'Gemiddelde woningbezetting',
  mf: 'Mutatiefactor',
  mp: 'Gecorrigeerde mutatiefactor',
  fc: 'Vruchtbaarheidscorrectie',
}

const GESLACHT_DISPLAY: Record<string, string> = {
  m: 'M',
  v: 'V',
  t: 'T',
}

type Mode = 'stad' | 'gebied'

interface Props {
  mode: Mode
}

const HistorischeDataView: FC<Props> = ({ mode }) => {
  const labels = mode === 'gebied' ? GEBIED_CATEGORIE_LABELS : STAD_CATEGORIE_LABELS
  const title = mode === 'gebied' ? 'Historische data — Gebied' : 'Historische data — Stad'

  const [selectedGemeente, setSelectedGemeente] = useState<GemeenteResponse | null>(null)
  const [meta, setMeta] = useState<BevolkingsdataMetaResponse | null>(null)
  const [pivotData, setPivotData] = useState<BevolkingsdataPivotResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table')

  const [categorie, setCategorie] = useState('')
  const [geslacht, setGeslacht] = useState('')
  const [doelgroep, setDoelgroep] = useState<DoelgroepKey | ''>('')
  const [gebiedCode, setGebiedCode] = useState('')
  const [jaarVan, setJaarVan] = useState('')
  const [jaarTot, setJaarTot] = useState('')
  const [leeftijdVan, setLeeftijdVan] = useState('')
  const [leeftijdTot, setLeeftijdTot] = useState('')
  const [bron, setBron] = useState('')

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const availableCategorieen = useMemo(() => {
    if (!meta) return []
    return meta.categorieen.filter((c) => labels[c])
  }, [meta, labels])

  const fetchMeta = useCallback(async (gemeenteSid: number) => {
    try {
      const result = await getJson<BevolkingsdataMetaResponse>(
        `${getUrl()}/gemeenten/${gemeenteSid}/bevolkingsdata/meta`,
      )
      setMeta(result)
      return result
    } catch (err) {
      console.error('Failed to load meta:', err)
      return null
    }
  }, [])

  const fetchPivotData = useCallback(
    async (
      gemeenteSid: number,
      cat: string,
      filters?: {
        geslacht?: string
        gebiedCode?: string
        jaarVan?: string
        jaarTot?: string
        leeftijdVan?: string
        leeftijdTot?: string
        bron?: string
      },
    ) => {
      if (!cat) return
      setLoading(true)
      try {
        const params = new URLSearchParams({ categorie: cat })
        if (filters?.geslacht) params.set('geslacht', filters.geslacht)
        if (filters?.gebiedCode) params.set('gebied_code', filters.gebiedCode)
        if (filters?.jaarVan) params.set('jaar_van', filters.jaarVan)
        if (filters?.jaarTot) params.set('jaar_tot', filters.jaarTot)
        if (filters?.leeftijdVan) params.set('leeftijd_van', filters.leeftijdVan)
        if (filters?.leeftijdTot) params.set('leeftijd_tot', filters.leeftijdTot)
        if (filters?.bron) params.set('bron', filters.bron)

        const result = await getJson<BevolkingsdataPivotResponse>(
          `${getUrl()}/gemeenten/${gemeenteSid}/bevolkingsdata/pivot?${params.toString()}`,
        )
        setPivotData(result)
        setPage(0)
      } catch (err) {
        console.error('Failed to load pivot data:', err)
        setPivotData(null)
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const resetFilters = useCallback(() => {
    setGeslacht('')
    setDoelgroep('')
    setGebiedCode('')
    setJaarVan('')
    setJaarTot('')
    setLeeftijdVan('')
    setLeeftijdTot('')
    setBron('')
  }, [])

  const handleGemeenteChange = useCallback(async (gem: GemeenteResponse | null) => {
    setSelectedGemeente(gem)
    setCategorie('')
    setMeta(null)
    setPivotData(null)
    resetFilters()
    rememberGemeente(gem)

    if (gem) {
      const metaResult = await fetchMeta(gem.sid)
      const cats = metaResult?.categorieen.filter((c) => labels[c]) ?? []
      if (cats.length > 0) {
        // Setting categorie triggers the debounced fetch effect below — no
        // direct fetchPivotData here (would cause a duplicate request).
        setCategorie(cats[0])
      }
    }
  }, [fetchMeta, labels, resetFilters])

  useLastGemeente(selectedGemeente, (gem) => {
    void handleGemeenteChange(gem)
  })

  const handleCategorieChange = (cat: string) => {
    setCategorie(cat)
  }

  const handleGebiedSelect = (code: string) => {
    setGebiedCode(code)
  }

  const handleExport = async () => {
    if (!selectedGemeente) return
    try {
      const params = new URLSearchParams()
      if (categorie) params.set('categorie', categorie)
      if (geslacht) params.set('geslacht', geslacht)
      if (gebiedCode) params.set('gebied_code', gebiedCode)
      if (bron) params.set('bron', bron)

      // authToken.getToken() already returns the value prefixed with "Bearer " —
      // re-adding it breaks JWT decode in the API middleware ("Invalid header padding").
      const token = authToken.getToken() || ''
      const response = await fetch(
        `${getUrl()}/gemeenten/${selectedGemeente.sid}/bevolkingsdata/export?${params.toString()}`,
        {
          headers: {
            'Authorization': token,
            'accept-language': 'nl',
          },
        },
      )

      if (!response.ok) throw new Error('Export failed')

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `historische_data_${mode}_${selectedGemeente.gm_code}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export failed:', err)
    }
  }

  const handleResetAndApply = () => {
    resetFilters()
    if (selectedGemeente && categorie) {
      void fetchPivotData(selectedGemeente.sid, categorie)
    }
  }

  const handleImportComplete = () => {
    setImportOpen(false)
    // Refresh meta so newly imported categorieen show up; the pivot effect
    // re-runs on its own because the data behind the current selection just
    // changed.
    if (selectedGemeente) void fetchMeta(selectedGemeente.sid)
  }

  // Apply client-side doelgroep bucketing on top of the server response.
  const yearColumns = useMemo(
    () => (pivotData?.columns ?? []).filter((c) => /^\d{4}$/.test(c)),
    [pivotData],
  )
  const aggregatedRows = useMemo(() => {
    if (!pivotData) return []
    return aggregateByDoelgroep(pivotData.data as never, doelgroep, yearColumns)
  }, [pivotData, doelgroep, yearColumns])

  // When a doelgroep is active, geslacht/bron are summed away — drop those
  // columns and rename "leeftijd" to "leeftijdsgroep" in the header.
  const displayColumns = useMemo(() => {
    if (!pivotData) return []
    if (!doelgroep) return pivotData.columns
    return pivotData.columns.filter((c) => c !== 'geslacht' && c !== 'bron')
  }, [pivotData, doelgroep])
  const leeftijdHeader = doelgroep ? 'Leeftijdsgroep' : 'Leeftijd'

  const totalRow = aggregatedRows.find((r) => r.leeftijd === 'Totaal')
  const bodyRows = aggregatedRows.filter((r) => r.leeftijd !== 'Totaal')
  const paginatedData = bodyRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  const totalRowCount = bodyRows.length

  const showBronFilter = mode === 'stad'
  const showTreeview = mode === 'gebied'

  // Auto-apply filter changes (debounced) so the user never needs a "Toepassen" knop.
  // The first effect-run fires immediately on gemeente/categorie change because those
  // already triggered an explicit fetch elsewhere; this effect handles only refinements.
  const filterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!selectedGemeente || !categorie) return undefined
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current)
    filterTimerRef.current = setTimeout(() => {
      void fetchPivotData(selectedGemeente.sid, categorie, {
        geslacht,
        gebiedCode,
        jaarVan,
        jaarTot,
        leeftijdVan,
        leeftijdTot,
        bron,
      })
    }, 300)
    return () => {
      if (filterTimerRef.current) clearTimeout(filterTimerRef.current)
    }
  }, [geslacht, bron, jaarVan, jaarTot, leeftijdVan, leeftijdTot, gebiedCode, categorie, selectedGemeente, fetchPivotData])

  const headerActions = selectedGemeente && meta ? (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<UploadIcon />}
        onClick={() => setImportOpen(true)}
      >
        Importeren
      </Button>
      <Button
        size="small"
        variant="outlined"
        startIcon={<DownloadIcon />}
        onClick={() => void handleExport()}
      >
        Exporteren
      </Button>
    </>
  ) : null

  const controls = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <FilterBar>
        <GemeenteAutocomplete
          value={selectedGemeente}
          onChange={(gem) => void handleGemeenteChange(gem)}
          sx={{ width: 260 }}
        />
        {selectedGemeente && meta && categorie && (
          <>
            <FormControl size="small" sx={{ width: 200 }}>
              <InputLabel>Onderdeel</InputLabel>
              <Select
                value={categorie}
                label="Onderdeel"
                onChange={(e) => handleCategorieChange(e.target.value)}
              >
                {availableCategorieen.map((c) => (
                  <MenuItem key={c} value={c}>
                    {labels[c] || c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 180 }}>
              <InputLabel>Doelgroep</InputLabel>
              <Select
                value={doelgroep}
                label="Doelgroep"
                onChange={(e) => setDoelgroep((e.target.value || '') as DoelgroepKey | '')}
              >
                <MenuItem value="">Geen</MenuItem>
                <MenuItem value="5jaar">{DOELGROEP_LABELS['5jaar']}</MenuItem>
                <MenuItem value="onderwijs">{DOELGROEP_LABELS.onderwijs}</MenuItem>
                <MenuItem value="doelgroep">{DOELGROEP_LABELS.doelgroep}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Jaar van"
              type="number"
              value={jaarVan}
              onChange={(e) => setJaarVan(e.target.value)}
              sx={{ width: 110 }}
            />
            <TextField
              size="small"
              label="Jaar tot"
              type="number"
              value={jaarTot}
              onChange={(e) => setJaarTot(e.target.value)}
              sx={{ width: 110 }}
            />
          </>
        )}
      </FilterBar>
      {selectedGemeente && meta && categorie && (
        <FilterBar>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={handleResetAndApply}
            sx={{ height: 40, flexShrink: 0 }}
          >
            Reset filters
          </Button>
          {showBronFilter && (
            <FormControl size="small" sx={{ width: 130 }}>
              <InputLabel>Bron</InputLabel>
              <Select
                value={bron}
                label="Bron"
                onChange={(e) => setBron(e.target.value)}
              >
                <MenuItem value="">Alle</MenuItem>
                <MenuItem value="import">Import</MenuItem>
                <MenuItem value="cbs">CBS</MenuItem>
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ width: 140 }}>
            <InputLabel>Geslacht</InputLabel>
            <Select
              value={geslacht}
              label="Geslacht"
              onChange={(e) => setGeslacht(e.target.value)}
            >
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="m">Mannen</MenuItem>
              <MenuItem value="v">Vrouwen</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Leeftijd van"
            type="number"
            value={leeftijdVan}
            onChange={(e) => setLeeftijdVan(e.target.value)}
            sx={{ width: 130 }}
          />
          <TextField
            size="small"
            label="Leeftijd tot"
            type="number"
            value={leeftijdTot}
            onChange={(e) => setLeeftijdTot(e.target.value)}
            sx={{ width: 130 }}
          />
        </FilterBar>
      )}
    </Box>
  )

  return (
    <DataPageLayout title={title} actions={headerActions} controls={controls}>
      <Grid container spacing={2} sx={{ flex: '1 1 0', minHeight: 0 }}>
        {showTreeview && selectedGemeente && meta && (
          <Grid
            item
            xs={12}
            md={3}
            sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            <GebiedTreeview
              gemeenteNaam={selectedGemeente.naam}
              nodes={meta.gebied_codes as GebiedNode[]}
              selectedCode={gebiedCode}
              onSelect={handleGebiedSelect}
            />
          </Grid>
        )}
        <Grid
          item
          xs={12}
          md={showTreeview && selectedGemeente && meta ? 9 : 12}
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : pivotData && aggregatedRows.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 0',
                minHeight: 0,
                backgroundColor: '#fff',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {viewMode === 'table' ? (
                <DataTable
                  columns={displayColumns}
                  rows={paginatedData}
                  totalRow={totalRow}
                  showTotalOnPage={page === 0}
                  leeftijdHeader={leeftijdHeader}
                />
              ) : (
                <Box sx={{ flex: '1 1 0', minHeight: 0 }}>
                  <HistorischeDataChart
                    columns={displayColumns}
                    data={aggregatedRows as never}
                    geslachtFilter={geslacht}
                  />
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                  flexShrink: 0,
                }}
              >
                <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
                {viewMode === 'table' && (
                  <TablePagination
                    component="div"
                    count={totalRowCount}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10))
                      setPage(0)
                    }}
                    rowsPerPageOptions={[25, 50, 100]}
                    labelRowsPerPage="Rijen per pagina:"
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} van ${count}`
                    }
                  />
                )}
              </Box>
            </Box>
          ) : selectedGemeente && categorie ? (
            <EmptyState message="Geen historische data gevonden voor deze selectie." />
          ) : !selectedGemeente ? (
            <EmptyState message="Selecteer een gemeente om historische data te bekijken." />
          ) : null}
        </Grid>
      </Grid>

      {selectedGemeente && categorie && (
        <ImportDialog
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onComplete={handleImportComplete}
          gemeenteSid={selectedGemeente.sid}
          gemeenteNaam={selectedGemeente.naam}
          categorie={categorie}
          categorieLabel={labels[categorie] ?? categorie}
        />
      )}
    </DataPageLayout>
  )
}

interface DataTableProps {
  columns: string[]
  rows: Record<string, unknown>[]
  totalRow?: Record<string, unknown>
  showTotalOnPage: boolean
  leeftijdHeader: string
}

const COL_HEADERS: Record<string, string> = {
  geslacht: 'Geslacht',
  bron: 'Bron',
}

const formatCell = (col: string, value: unknown): string => {
  if (value == null || value === '') return ''
  if (col === 'geslacht' && typeof value === 'string') {
    return GESLACHT_DISPLAY[value.toLowerCase()] ?? value
  }
  if (typeof value === 'number') {
    return value.toLocaleString('nl-NL', { maximumFractionDigits: 2 })
  }
  return String(value)
}

const DataTable: FC<DataTableProps> = ({
  columns, rows, totalRow, showTotalOnPage, leeftijdHeader,
}) => (
  <TableContainer sx={{ flex: '1 1 0', minHeight: 0, overflow: 'auto' }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          {columns.map((col) => (
            <TableCell
              key={col}
              sx={{
                fontWeight: 700,
                color: xxllncColor.dark100,
                backgroundColor: '#fff',
                whiteSpace: 'nowrap',
              }}
            >
              {col === 'leeftijd' ? leeftijdHeader : (COL_HEADERS[col] ?? col)}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, idx) => (
          <TableRow key={idx} hover>
            {columns.map((col) => (
              <TableCell key={col} sx={{ whiteSpace: 'nowrap' }}>
                {formatCell(col, row[col])}
              </TableCell>
            ))}
          </TableRow>
        ))}
        {totalRow && showTotalOnPage && (
          <TableRow>
            {columns.map((col, idx) => (
              <TableCell
                key={col}
                sx={{
                  fontWeight: 700,
                  // Solid background + per-cell sticky (TableRow itself cannot be sticky
                  // in browsers — each cell pins individually so the underlying rows do
                  // not bleed through).
                  backgroundColor: xxllncColor.light40,
                  borderTop: `2px solid ${xxllncColor.dark20}`,
                  position: 'sticky',
                  bottom: 0,
                  whiteSpace: 'nowrap',
                  zIndex: 2,
                }}
              >
                {col === 'leeftijd' && idx === columns.indexOf('leeftijd')
                  ? 'Totaal'
                  : formatCell(col, totalRow[col])}
              </TableCell>
            ))}
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
)

interface EmptyStateProps {
  message: string
}

const EmptyState: FC<EmptyStateProps> = ({ message }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      backgroundColor: '#fff',
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      p: 4,
    }}
  >
    <Typography color="textSecondary">{message}</Typography>
  </Box>
)

interface ViewModeToggleProps {
  viewMode: 'table' | 'chart'
  onChange: (m: 'table' | 'chart') => void
}

const ViewModeToggle: FC<ViewModeToggleProps> = ({ viewMode, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
    <Typography
      variant="caption"
      sx={{ color: xxllncColor.dark60, fontWeight: 500, letterSpacing: 0.4 }}
    >
      Weergeven als
    </Typography>
    <ToggleButtonGroup
      value={viewMode}
      exclusive
      size="small"
      onChange={(_, v: 'table' | 'chart' | null) => {
        if (v) onChange(v)
      }}
    >
      <ToggleButton value="table" aria-label="Toon tabel">
        <TableChartIcon fontSize="small" sx={{ mr: 0.5 }} />
        Tabel
      </ToggleButton>
      <ToggleButton value="chart" aria-label="Toon grafiek">
        <ShowChartIcon fontSize="small" sx={{ mr: 0.5 }} />
        Grafiek
      </ToggleButton>
    </ToggleButtonGroup>
  </Box>
)

export default HistorischeDataView
